// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled, { withTheme } from 'styled-components';
import { bindActionCreators } from 'redux';
import { translate } from 'react-i18next';
import RaisedButton from 'material-ui/RaisedButton';
import Dialog from 'material-ui/Dialog/Dialog';

import { openSnackbar } from 'common/modules/ui/actions/uiActions';
import PropositionDetailsContent from 'common/modules/proposition/components/PropositionDetailsContent';
import Radio from 'common/domain/radio';
import LoadingSpinner from 'common/components/LoadingSpinner';
import FixedActionsBar from 'common/components/FixedActionsBar';
import Campaign from 'common/modules/campaign/domain/Campaign';
import CampaignMetrics from 'common/modules/campaign/domain/CampaignMetrics';
import RadioUser from 'statsradio/domain/RadioUser';
import FileTooLargeError from 'common/domain/FileTooLargeError';
import * as Routes from 'statsradio/helpers/constant/Routes';

import CampaignAPI, { MAX_AUDIO_FILE_SIZE_IN_MB } from './services/CampaignAPI';
import { getRadioUser, getToken } from '../authentication/session/selectors/sessionSelectors';
import Permissions from '../authorisation/Permissions';
import AuthorisedComponent from '../authorisation/AuthorisedComponent';
import Overview from './CampaignOverview';
import LinkCampaignDialog from './components/LinkCampaignDialog';
import CompleteCampaignDialog from './components/CompleteCampaignDialog';
import ActivateCampaignInFolloButton from './components/ActivateCampaignInFolloButton';
import PrintCampaignButton from './printing/PrintCampaignButton';
import { ExternalCampaignListing } from './domain/externalCampaign';
import CampaignLinkingButton from './components/CampaignLinkingButton';

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const LeftActionButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const RightActionButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CenterWrapper = styled.div`
  justify-content: center;
  text-align: center;
  margin-top: 25vh;
  margin-left: auto;
  margin-right: auto;
`;

type Props = {
  history: any;
  match: Object;
  location: Object;

  reference: string,
  radio: Radio,
  token: string,
  user: RadioUser,
  openLinkingDialog?: boolean;
  openCompletionDialog?: boolean,

  t: any;
  theme: any;

  actions: {
    openSnackbar: (type: string, message: string) => mixed;
  }
};

type State = {
  showProposition: boolean,
  isEditingCampaignMessage: boolean,
  isLinkingExternalCampaign: boolean,
  isCompletingCampaign: boolean,
  metrics: ?CampaignMetrics,
  isFetchingMetrics: boolean,
  isFetchingCampaign: boolean,
  hasError: boolean,
  campaign?: Campaign,
  campaignMessage: ?string,
};

class CampaignDetail extends Component<Props, State> {
  state: State = {
    showProposition: false,
    isEditingCampaignMessage: false,
    isLinkingExternalCampaign: false,
    isCompletingCampaign: false,
    metrics: undefined,
    isFetchingCampaign: false,
    isFetchingMetrics: false,
    hasError: false,
    campaign: undefined,
    campaignMessage: '',
  };

  constructor(props: Props) {
    super(props);

    (this: any).uploadAudioFile = this.uploadAudioFile.bind(this);
  }

  componentDidMount() {
    this.setState({
      isLinkingExternalCampaign: this.props.openLinkingDialog || false,
      isCompletingCampaign: this.props.openCompletionDialog || false,
    });
    this.initialFetch();
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.radio !== this.props.radio) {
      this.props.history.push('/campaigns');
    }
  }

  async initialFetch() {
    await this.fetchCampaign();
  }

  async fetchCampaign() {
    const { user, token, radio, reference } = this.props;
    this.setState({
      isFetchingCampaign: true, metrics: undefined, hasError: false, campaign: undefined,
    });

    try {
      let campaign;
      if (user.isProduction()) {
        campaign = await CampaignAPI.fetchCampaignForProduction(token, radio.callSign, reference);
      } else {
        campaign = await CampaignAPI.fetchCampaign(token, radio.callSign, reference);
      }
      this.setState({ isFetchingCampaign: false, campaign });

      if (!campaign.isBooked()) {
        await this.fetchCampaignMetrics(campaign.reference);
      }
    } catch (error) {
      this.setState({ isFetchingCampaign: false, hasError: true });
    }
  }

  async fetchCampaignMetrics(campaignReference: string) {
    const { token, radio, user } = this.props;

    if (!user.hasPermission(Permissions.MANAGE_CAMPAIGNS) || !user.hasPermission(Permissions.ACCESS_CAMPAIGNS)) {
      return;
    }

    this.setState({
      isFetchingMetrics: true, metrics: undefined, hasError: false,
    });

    try {
      const metrics = await CampaignAPI.fetchCampaignMetrics(token, radio, campaignReference);
      this.setState({ isFetchingMetrics: false, metrics });
    } catch (error) {
      this.setState({ isFetchingMetrics: false, hasError: true });
    }
  }

  onViewSpots = () => {
    this.props.history.push(`/spots/${this.props.match.params.reference}`);
  };

  onViewProposition = () => {
    this.setState({ showProposition: true });
  };

  onLinkCampaign = () => {
    this.setState({ isLinkingExternalCampaign: true });
  };

  onCloseProposition = () => {
    this.setState({ showProposition: false });
  };

  onOpenCompleteDialog = () => {
    this.setState({ isCompletingCampaign: true });
  };

  onCloseCompleteDialog = () => {
    this.setState({ isCompletingCampaign: false });
  };

  onCampaignCompletion = (campaign: Campaign) => {
    this.setState({ campaign, isCompletingCampaign: false });
  };

  deleteCampaign = async () => {
    const { radio, reference, token, t, actions, history } = this.props;
    try {
      await CampaignAPI.deleteCampaign(token, radio.callSign, reference);
      history.push(Routes.CAMPAIGNS);
    } catch (e) {
      actions.openSnackbar('error', t('campaigns:couldNotDeleteCampaign'));
    }
  }

  removeAudioFile = async () => {
    const { radio, reference, token, t, actions } = this.props;
    try {
      const campaign = await CampaignAPI.removeAudioFileMessage(token, radio.callSign, reference);
      this.setState({ campaign });
    } catch (e) {
      actions.openSnackbar('error', t('commonCampaign:errorRemoveAudioFile'));
    }
  };

  editCampaignMessage = async (message: string) => {
    const { radio, reference, token, t, actions } = this.props;
    try {
      const campaign = await CampaignAPI.editCampaignMessage(token, radio.callSign, reference, message);
      this.setState({ campaign });
    } catch (e) {
      actions.openSnackbar('error', t('commonCampaign:errorMessageModification'));
    }
  };

  async uploadAudioFile(file: File) {
    const { radio, reference, token, t, actions } = this.props;


    try {
      const campaign = await CampaignAPI.uploadAudioFileMessage(token, radio.callSign, reference, file);
      this.setState({ campaign });
    } catch (e) {
      if (e instanceof FileTooLargeError) {
        actions.openSnackbar('error', t('campaigns:audioFileTooLarge', { maxFileSizeInMegabytes: MAX_AUDIO_FILE_SIZE_IN_MB }));
      } else {
        actions.openSnackbar('error', t('commonCampaign:errorUploadingAudioFile'));
      }
    }
  }

  onCloseLinkingDialog = () => {
    this.setState({ isLinkingExternalCampaign: false });
  };

  onLinkSuccess = (linkedExternalCampaigns: ExternalCampaignListing[]) => {
    const { campaign } = this.state;
    if (campaign) {
      campaign.link(linkedExternalCampaigns);
    }
  };

  render() {
    const { t, history, theme, token, radio } = this.props;
    const { metrics, isFetchingCampaign, hasError, campaign, isFetchingMetrics } = this.state;

    const closePropositionButton = [
      <RaisedButton primary label={t('close')} onClick={this.onCloseProposition} key="close" />,
    ];

    return (
      <div>
        {isFetchingCampaign &&
          <CenterWrapper>
            <LoadingSpinner size="large" />
          </CenterWrapper>
        }

        {!isFetchingCampaign && !campaign &&
          <CenterWrapper>
            <span style={theme.fonts.style.smallCardTitle}>
              {t('noDataAvailable')}
            </span>
          </CenterWrapper>
        }

        {!isFetchingCampaign && campaign &&
          <div>
            <Overview
              colors={theme.colors}
              campaign={campaign}
              metrics={metrics}
              campaignNotStarted={{
                image: theme.images.emptyCampaign,
                buttonText: t('campaigns:seeCampaignsButton'),
                buttonCallback: () => history.push(Routes.CAMPAIGNS),
              }}
              isFetchingMetrics={isFetchingMetrics}
              hasMetricsError={hasError}
              editMessage={this.editCampaignMessage}
              uploadAudioFileMessage={this.uploadAudioFile}
              removeAudioFileMessage={this.removeAudioFile}
            />

            <AuthorisedComponent permissions={[Permissions.ACCESS_SALES, Permissions.ACCESS_SPOTS, Permissions.MANAGE_CAMPAIGNS]}>
              <FixedActionsBar position="BOTTOM">
                <ButtonWrapper>
                  <LeftActionButtonsWrapper>
                    <PrintCampaignButton campaign={campaign} metrics={metrics} />
                  </LeftActionButtonsWrapper>
                  <RightActionButtonsWrapper>
                    {campaign.canBeCompleted() &&
                      <React.Fragment>
                        <RaisedButton primary label={t('commonCampaign:completeCampaign')} onClick={this.onOpenCompleteDialog} />
                        <CompleteCampaignDialog
                          token={token}
                          campaign={campaign}
                          metrics={metrics}
                          visible={this.state.isCompletingCampaign}
                          onClose={this.onCloseCompleteDialog}
                          onSuccess={this.onCampaignCompletion}
                        />
                      </React.Fragment>
                    }
                    <ActivateCampaignInFolloButton
                      campaign={campaign}
                    />
                    {!campaign.isCompleted() &&
                      <AuthorisedComponent permissions={Permissions.ACCESS_SPOTS}>
                        <RaisedButton primary label={t('campaigns:manageSpots')} onClick={this.onViewSpots} />
                      </AuthorisedComponent>
                    }
                    {campaign.proposition &&
                      <AuthorisedComponent permissions={Permissions.ACCESS_SALES}>
                        <RaisedButton primary label={t('commonCampaign:viewProposition')} onClick={this.onViewProposition} />
                      </AuthorisedComponent>
                    }
                    {!campaign.isCompleted() && radio.hasConnector() &&
                      <React.Fragment>
                        <CampaignLinkingButton
                          campaign={campaign}
                          onClick={this.onLinkCampaign}
                        />
                        <LinkCampaignDialog
                          campaign={campaign}
                          open={this.state.isLinkingExternalCampaign}
                          onLinkSuccess={this.onLinkSuccess}
                          onClose={this.onCloseLinkingDialog}
                        />
                      </React.Fragment>
                    }
                    <AuthorisedComponent permissions={Permissions.DELETE_CAMPAIGNS}>
                      <RaisedButton
                        buttonStyle={{ backgroundColor: theme.colors.delete }}
                        labelColor={theme.colors.tertiaryText}
                        label={t('delete')}
                        onClick={this.deleteCampaign}
                      />
                    </AuthorisedComponent>
                  </RightActionButtonsWrapper>
                </ButtonWrapper>
              </FixedActionsBar>
            </AuthorisedComponent>
          </div>
        }
        {!isFetchingCampaign && campaign && campaign.proposition &&
          <Dialog
            actions={closePropositionButton}
            autoScrollBodyContent
            bodyStyle={{ backgroundColor: theme.colors.primaryBackground }}
            contentStyle={{ width: '90%', maxWidth: '90%' }}
            title={campaign.name}
            titleStyle={{ fontSize: theme.fonts.style.secondary.fontSize, color: theme.colors.tertiaryText, backgroundColor: theme.colors.primary }}
            open={this.state.showProposition}
            onRequestClose={this.onCloseProposition}
          >
            <PropositionDetailsContent hideTitle proposition={campaign.proposition} />
          </Dialog>
        }
      </div>
    );
  }
}

function mapLocationStateToProps(location: any) {
  return {
    openLinkingDialog: location.state ? location.state.openLinkingDialog : false,
    openCompletionDialog: location.state ? location.state.openCompletionDialog : false,
  };
}

function mapStateToProps(state, ownProps: Props) {
  return {
    reference: ownProps.match.params.reference,
    token: getToken(state),
    radio: state.session.radio,
    user: getRadioUser(state),
    ...mapLocationStateToProps(ownProps.location),
  };
}

function mapDispatchToProps(dispatch: *) {
  return {
    actions: bindActionCreators({
      openSnackbar,
    }, dispatch),
  };
}

export default withTheme(translate('commonCampaign', 'campaigns')(connect(mapStateToProps, mapDispatchToProps)(withRouter(CampaignDetail))));



// WEBPACK FOOTER //
// ./src/statsradio/modules/campaigns/CampaignDetailPage.js